import {
  BoardAutomationOnCompleteDraft,
  BoardAutomationOnMoveDraft,
  TaskTrackerBoardAutomation,
  UpdateTaskTrackerBoardAutomationOnCompleteRequest,
  UpdateTaskTrackerBoardAutomationOnMoveRequest,
} from '../store/taskTracker.types'

export const TASK_TRACKER_AUTOMATION_TRIGGER_COMPLETED = 'task:completed'
export const TASK_TRACKER_AUTOMATION_TRIGGER_MOVED = 'task:moved'

export const createEmptyBoardAutomationOnCompleteDraft =
  (): BoardAutomationOnCompleteDraft => ({
    isActive: false,
    targetColumnId: null,
  })

export const createEmptyBoardAutomationOnMoveDraft =
  (): BoardAutomationOnMoveDraft => ({
    isActive: false,
    completeOnMove: false,
    columnIds: [],
  })

const readNumberParam = (params: Record<string, unknown>, key: string) => {
  const raw = params[key]
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const readNumberArrayParam = (params: Record<string, unknown>, key: string) => {
  const raw = params[key]
  if (!Array.isArray(raw)) return []

  return raw
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item))
}

export const mapCompletedAutomationToDraft = (
  automation?: TaskTrackerBoardAutomation,
): BoardAutomationOnCompleteDraft => {
  if (!automation) return createEmptyBoardAutomationOnCompleteDraft()

  const draft: BoardAutomationOnCompleteDraft = {
    isActive: automation.isActive,
    targetColumnId: null,
  }

  automation.actions.forEach((action) => {
    if (action.actionType === 'move_task') {
      draft.targetColumnId = readNumberParam(action.params, 'target_column_id')
    }
  })

  return draft
}

export const mapMovedAutomationToDraft = (
  automation?: TaskTrackerBoardAutomation,
): BoardAutomationOnMoveDraft => {
  if (!automation) return createEmptyBoardAutomationOnMoveDraft()

  const draft: BoardAutomationOnMoveDraft = {
    isActive: automation.isActive,
    completeOnMove: false,
    columnIds: [],
  }

  automation.actions.forEach((action) => {
    if (action.actionType === 'complete_task') {
      draft.completeOnMove = true
      draft.columnIds = readNumberArrayParam(action.params, 'column_ids')
    }
  })

  return draft
}

export const serializeBoardAutomationOnCompleteDraft = (
  draft: BoardAutomationOnCompleteDraft,
) =>
  JSON.stringify({
    isActive: draft.isActive,
    targetColumnId: draft.targetColumnId,
  })

export const serializeBoardAutomationOnMoveDraft = (
  draft: BoardAutomationOnMoveDraft,
) =>
  JSON.stringify({
    isActive: draft.isActive,
    completeOnMove: draft.completeOnMove,
    columnIds: [...draft.columnIds].sort((a, b) => a - b),
  })

export const buildOnCompleteAutomationPayload = (
  draft: BoardAutomationOnCompleteDraft,
): UpdateTaskTrackerBoardAutomationOnCompleteRequest => {
  if (draft.targetColumnId == null) {
    throw new Error('Выберите колонку для переноса при выполнении')
  }

  return {
    is_active: true,
    move_on_complete: true,
    target_column_id: draft.targetColumnId,
  }
}

export const buildOnMoveAutomationPayload = (
  draft: BoardAutomationOnMoveDraft,
): UpdateTaskTrackerBoardAutomationOnMoveRequest => {
  if (!draft.completeOnMove || draft.columnIds.length === 0) {
    throw new Error('Выберите колонки для автовыполнения при перемещении')
  }

  return {
    is_active: true,
    complete_on_move: true,
    complete_on_move_column_ids: draft.columnIds,
  }
}

const mapAutomationActionsToUpdateBody = (
  automation: TaskTrackerBoardAutomation,
) =>
  automation.actions
    .slice()
    .sort((a, b) => a.sorting - b.sorting)
    .map((action) => ({
      action_type: action.actionType,
      params: action.params,
      sorting: action.sorting,
    }))

export const buildDisableAutomationPayload = (
  automation: TaskTrackerBoardAutomation,
) => ({
  automation_id: automation.id,
  trigger_event: automation.triggerEvent,
  is_active: false,
  actions: mapAutomationActionsToUpdateBody(automation),
})

export const buildOnCompleteAutomationUpdatePayload = (
  draft: BoardAutomationOnCompleteDraft,
  existingAutomation?: TaskTrackerBoardAutomation,
) => {
  if (!draft.isActive) {
    return existingAutomation
      ? buildDisableAutomationPayload(existingAutomation)
      : null
  }

  return buildOnCompleteAutomationPayload(draft)
}

export const buildOnMoveAutomationUpdatePayload = (
  draft: BoardAutomationOnMoveDraft,
  existingAutomation?: TaskTrackerBoardAutomation,
) => {
  if (!draft.isActive) {
    return existingAutomation
      ? buildDisableAutomationPayload(existingAutomation)
      : null
  }

  return buildOnMoveAutomationPayload(draft)
}

export const findAutomationByTrigger = (
  automations: TaskTrackerBoardAutomation[],
  triggerEvent: string,
) => automations.find((item) => item.triggerEvent === triggerEvent)
